import { isProblem, keyAnswer, problemDetail } from '@/shared/api'

/*
 * ИСХОДЫ УПАВШЕЙ ПОПЫТКИ — и у каждого свои слова и действия (что помнит ключ —
 * `components/parameters.yaml#/IdempotencyKey`):
 *
 *   сбой сети, 5xx       клиенту исход неизвестен: запрос мог дойти → «Повторить» тем же ключом
 *                        (дошло — сервер вернёт первый ответ, нет — отправит)
 *   401, 403, 404, 408,  до действия запрос не дошёл: сессия, доступ (переписка другой компании —
 *   409, 429             404), «попробуйте позже» (409 — первый запрос ещё идёт) → «Повторить»
 *   прочие 4xx — отказ   гостю не ушло, и повтор упрётся в тот же отказ → «Править»
 *
 * ⚠️ Исход, неизвестный и серверу (`idempotency-outcome-unknown`), и сутки ключа, прошедшие после
 * попытки, которая могла дойти, тем же ключом не решить — «проверьте ленту» и «Править». 401 в
 * очереди не бывает: попытки уходят вместе с кешем, и говорит тост.
 */
const NOT_REACHED = new Set([401, 403, 404, 408, 409, 429])

/** Сутки ключа от первого запроса — с часом запаса: `Date.now()` — настенные часы, их переводят назад. */
const KEY_TTL_MS = 23 * 60 * 60 * 1000

/** Попытки одного сообщения: отказ последней и отказы всех, её в том числе. */
type Attempts = { error: unknown; failures: readonly unknown[] }

/** Могла ли какая-то попытка дойти до гостя: хоть одна кончилась сбоем сети или 5xx. */
export function mayHaveArrived(failures: readonly unknown[]): boolean {
  return failures.some((error) => !isProblem(error) || error.status >= 500)
}

/** Отказ: гостю не ушло, и повтор того же упрётся в тот же отказ — выход «Править». */
export function isRefusal({ error }: { error: unknown }): boolean {
  return isProblem(error) && error.status >= 400 && error.status < 500 && !NOT_REACHED.has(error.status)
}

/** Прошли сутки ключа, а сообщение могло дойти: повтор тем же ключом его уже не узнает. */
export function keyExpired(message: Attempts & { sentAt: number }, now: number): boolean {
  return mayHaveArrived(message.failures) && now - message.sentAt > KEY_TTL_MS
}

/** Можно ли повторить тем же ключом — иначе «Править», и решает менеджер, проверив ленту. */
export function retryable(message: Attempts & { sentAt: number }, now = Date.now()): boolean {
  return !isRefusal(message) && keyAnswer(message.error) !== 'unknown' && !keyExpired(message, now)
}

/** Ответ сервера — его словами целиком: заголовок, за ним пояснение. У 5xx слов нет, кроме ключа. */
function serverWords(error: unknown): string | null {
  if (!isProblem(error) || ((error.status < 400 || error.status >= 500) && !keyAnswer(error))) return null
  const detail = problemDetail(error)
  return detail ? `${sentence(error.title)} ${detail}` : error.title
}

const sentence = (text: string) => (/[.!?…]$/.test(text) ? text : `${text}.`)
// Слова сервера посреди фразы — в кавычках: регистр их не наш («QR-сессия», «WhatsApp»).
const quote = (words: string) => `«${words.trim().replace(/([^.])\.$/, '$1')}»`
const upperFirst = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)

const UNKNOWN = 'Не знаем, дошло ли'

/**
 * Строка под упавшим сообщением. Отказ — словами сервера. Могла дойти хоть одна попытка — «Не
 * знаем, дошло ли»: ответ повтора, не дошедшего до действия, этого не отменяет, а лишь называет,
 * почему повтор не прошёл; не решит его и повтор — «проверьте ленту». Иначе — словами сервера.
 * Выполнено, а ответ не собран (busy на 5xx) — словами сервера: ушло.
 */
export function unsentLine(message: Attempts & { sentAt: number }, now = Date.now()): string {
  const words = serverWords(message.error)
  if (isRefusal(message) || !mayHaveArrived(message.failures)) return words ?? UNKNOWN
  if (keyAnswer(message.error) === 'unknown') return `${UNKNOWN} — проверьте ленту`
  if (keyExpired(message, now)) return `${UNKNOWN} — прошли сутки, проверьте ленту`
  // Выполнено, а ответ не собран (busy на 5xx): ушло — повтор тем же ключом получит ответ.
  if (keyAnswer(message.error) === 'busy' && mayHaveArrived([message.error])) return words ?? UNKNOWN
  return words ? `${UNKNOWN}; повтор не прошёл: ${quote(words)}` : UNKNOWN
}

/** Время первого «Отправить»; не сегодняшнее — с датой: упавшее живёт, пока открыта вкладка. */
export function unsentTime(sentAt: number, now = Date.now()): string {
  const at = new Date(sentAt)
  const time = at.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  if (at.toDateString() === new Date(now).toDateString()) return time
  return `${at.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}, ${time}`
}

/** Сколько текста называет сообщение в тосте: хватает узнать, не хватает растянуть заголовок. */
const SNIPPET = 40

/** Начало сообщения — по границе слова и без висящей пунктуации. */
function snippet(text: string): string {
  const flat = text.replace(/\s+/g, ' ').trim()
  if (flat.length <= SNIPPET) return flat
  const cut = flat.slice(0, SNIPPET + 1)
  const space = cut.lastIndexOf(' ')
  const head = space > SNIPPET / 2 ? cut.slice(0, space) : cut.slice(0, SNIPPET)
  return `${head.replace(/[\s,.;:!?—-]+$/, '')}…`
}

/**
 * Предмет тоста — какое сообщение и кому. Гость через «·»: имя из мессенджера не склоняется, и
 * «гостю Марина Лебедева» читалось бы с ошибкой. Безымянного — контактом: тост встаёт поверх
 * другого экрана, и «Без имени» не сказало бы, кому.
 */
export function unsentSubject(guest: { name?: string; contact?: string } | undefined, text: string): string {
  const who = guest?.name?.trim() || guest?.contact?.trim()
  return who ? `Сообщение «${snippet(text)}» · ${who}` : `Сообщение «${snippet(text)}»`
}

/** Объявление читалке об упавшем на глазах: предмет, затем строка отказа — со своей заглавной. */
export function unsentAnnouncement(message: Attempts & { text: string; sentAt: number }, now = Date.now()): string {
  return sentence(`${sentence(unsentSubject(undefined, message.text))} ${unsentLine(message, now)}`)
}

/** «Повторить», а сутки ключа уже прошли — кнопка осталась с прошлой отрисовки: почему не ушло. */
export function unsentStaleToast(subject: string): { title: string; description: string } {
  return {
    title: UNKNOWN,
    description: `${subject} — прошли сутки, и повтор уже не узнаётся: проверьте ленту, прежде чем писать снова.`,
  }
}

/**
 * Что говорит тост о сообщении, чьей строки отказа не видно. `failures` — отказы всех попыток,
 * последняя в том числе. `waits` — попытки ещё в кеше; нет их — значит, кеш снёс выход из аккаунта
 * (или истекла сессия), и текст не сохранился.
 */
export function unsentToast(
  failures: readonly unknown[],
  subject: string,
  waits: boolean,
): { title: string; description: string } {
  const error = failures[failures.length - 1]
  const arrived = mayHaveArrived(failures)

  if (isProblem(error) && error.status === 401) {
    // «Отправьте снова» — это новый ключ: если попытка дошла, гость получил бы сообщение дважды.
    return arrived
      ? {
          title: UNKNOWN,
          description: `${subject} — сессия закончилась, а сообщение могло дойти: войдите и проверьте переписку, прежде чем писать снова. Текст не сохранился.`,
        }
      : {
          title: subject,
          description: 'Не отправлено: сессия закончилась. Войдите и отправьте снова — текст не сохранился.',
        }
  }

  if (keyAnswer(error) === 'unknown') {
    return {
      title: UNKNOWN,
      description: waits
        ? `${subject} — сервер не знает, ушло ли оно: проверьте ленту, прежде чем писать снова. Текст ждёт в переписке.`
        : `${subject} — сервер не знает, ушло ли оно: проверьте переписку, когда войдёте снова. Текст не сохранился.`,
    }
  }

  const words = serverWords(error)
  const refusal = isRefusal({ error })
  const rest = waits
    ? 'оно ждёт в переписке, и «Повторить» не отправит его дважды.'
    : 'текст не сохранился; проверьте переписку, когда войдёте снова.'
  // Выполнено, а ответ не собран (busy на 5xx): ушло, и незнания тут нет — слова сервера.
  if (keyAnswer(error) === 'busy' && mayHaveArrived([error]) && words) {
    return { title: subject, description: `${sentence(words)} ${upperFirst(rest)}` }
  }
  if (!words || (arrived && !refusal)) {
    // Повтор, не дошедший до действия, называет причину: иначе он неотличим от того же сбоя.
    return {
      title: UNKNOWN,
      description: words ? `${subject} — повтор не прошёл: ${quote(words)}. ${upperFirst(rest)}` : `${subject} — ${rest}`,
    }
  }

  const tail = !waits
    ? 'Текст не сохранился.'
    : refusal
      ? 'Текст ждёт в переписке: «Править» вернёт его в поле.'
      : 'Оно ждёт в переписке — «Повторить» отправит его.'
  return { title: subject, description: `${sentence(words)} ${tail}` }
}
